import * as fs from 'fs';
import * as fsp from 'fs/promises';
import { dirname } from 'path';

/**
 * Persistent state for the spend-tracker observer. One row per
 * dispatched job that produced reportable usage; the tracker sums them
 * inside the period window before each preflight.
 *
 * v0.1 stores the whole state in a single JSON document. The volume is
 * trivial (low thousands of rows per month) and a complete rewrite is the
 * simplest crash-safe story: write to a tmp file, fsync, rename.
 */
export interface SpendEntry {
  /** ISO 8601 timestamp of the dispatch completion. */
  at: string;
  jobId: string;
  inputTokens: number;
  outputTokens: number;
  costUsd?: number;
}

export interface SpendCapsState {
  dailyTokens: number;
  monthlyTokens: number;
  dailyUsd?: number;
  monthlyUsd?: number;
}

export interface SpendStateDocument {
  schemaVersion: 1;
  caps: SpendCapsState;
  entries: SpendEntry[];
}

const EMPTY_DOC = (caps: SpendCapsState): SpendStateDocument => ({
  schemaVersion: 1,
  caps,
  entries: [],
});

/**
 * Reads and writes the spend-state document under an in-process mutex.
 * Callers should always go through `withState`; bare `load` / `save` are
 * exposed for tests and CLI tools.
 */
export class SpendStore {
  private readonly path: string;
  private chain: Promise<unknown> = Promise.resolve();

  constructor(path: string) {
    this.path = path;
  }

  /**
   * Run `mutate` against the current document and persist the result.
   * Concurrent calls are serialised internally; the mutator sees a
   * snapshot loaded after the previous call's save completes.
   */
  async withState<T>(
    fallbackCaps: SpendCapsState,
    mutate: (doc: SpendStateDocument) => Promise<T> | T,
  ): Promise<T> {
    const next = this.chain.then(async () => {
      const doc = this.loadOrInit(fallbackCaps);
      const value = await mutate(doc);
      await this.save(doc);
      return value;
    });
    this.chain = next.catch(() => undefined);
    return next as Promise<T>;
  }

  /** Read the current document without taking the mutex. */
  read(fallbackCaps: SpendCapsState): SpendStateDocument {
    return this.loadOrInit(fallbackCaps);
  }

  private loadOrInit(fallbackCaps: SpendCapsState): SpendStateDocument {
    if (!fs.existsSync(this.path)) {
      return EMPTY_DOC(fallbackCaps);
    }
    try {
      const raw = fs.readFileSync(this.path, 'utf-8');
      const parsed = JSON.parse(raw) as Partial<SpendStateDocument>;
      if (parsed && parsed.schemaVersion === 1 && parsed.caps && Array.isArray(parsed.entries)) {
        return {
          schemaVersion: 1,
          caps: { ...parsed.caps } as SpendCapsState,
          entries: parsed.entries.slice() as SpendEntry[],
        };
      }
    } catch {
      // fall through to a fresh doc; the previous state was unreadable.
    }
    return EMPTY_DOC(fallbackCaps);
  }

  /** Force-replace the caps on disk. Used by `<brand> caps set`. */
  async setCaps(caps: SpendCapsState): Promise<void> {
    await this.withState(caps, (doc) => {
      doc.caps = { ...caps };
    });
  }

  private async save(doc: SpendStateDocument): Promise<void> {
    await fsp.mkdir(dirname(this.path), { recursive: true });
    const tmp = `${this.path}.tmp`;
    const fd = await fsp.open(tmp, 'w', 0o600);
    try {
      await fd.write(JSON.stringify(doc, null, 2));
      await fd.sync();
    } finally {
      await fd.close();
    }
    await fsp.rename(tmp, this.path);
  }
}
